/**
 * La liste des chantiers, et la fiche de chacun.
 *
 * *Demandé par le métier le 13/08/2026 : « il faudrait avoir un menu avec tous les
 * chantiers, il faudra penser à rajouter le statut du chantier comme décrit dans le
 * PRD, voir date de démarrage et date de fin », et « après avoir intégré un déboursé
 * il faut pouvoir saisir l'adresse complète du chantier ».*
 *
 * C'est l'écran « Liste des chantiers » du PRD §5 — « 15 à 30 chantiers actifs,
 * statut selon le cycle de vie (§4.9), indicateurs clés » — et, quand on déplie une
 * ligne, l'écran **PARAMS** du même tableau.
 *
 * ## Trois partis pris
 *
 * 1. **La fiche s'ouvre SOUS la ligne du chantier**, pas dans un panneau ailleurs.
 *    C'est la leçon des livraisons, deux fois payée la veille : une saisie placée en
 *    fin de page s'ouvre hors de l'écran sur une liste longue, et personne ne la
 *    trouve. Ce qu'on modifie se voit à côté de ce qu'on lit.
 *
 * 2. **Un bouton « Enregistrer », et non une sauvegarde champ par champ** comme dans
 *    les référentiels. Ce n'est pas un choix de confort : latitude et longitude vont
 *    ensemble — le serveur refuse l'une sans l'autre —, et une sauvegarde au champ
 *    perdu de vue rejetterait la première des deux saisies systématiquement.
 *
 * 3. **La date de fin ne se saisit pas, elle s'affiche.** Elle tombe du planning
 *    (PRD §5, « date de fin prévue » du bilan) : la dernière barre, la dernière
 *    livraison, converties en date réelle. Une colonne saisie à côté finirait par
 *    contredire le Gantt, et c'est le Gantt qui a raison.
 */
import { Fragment, useEffect, useState } from "react";
import type { JSX } from "react";
import {
  estDateCivile,
  ferie,
  formaterDate,
  formaterDateLongue,
  formaterMontant,
  jourDeLaSemaine,
  STATUTS_CHANTIER,
  STATUTS_STOCKABLES,
} from "@planif/moteur";
import type {
  BaremePeriode,
  BaremesTransport,
  ChantierListe,
  CommuneChantier,
  DepoListe,
  DistanceSiteChantier,
  SansZone,
  ZoneRetenue,
} from "@planif/moteur";
import {
  adresseClasseurPlanning,
  chercherCommunes,
  modifierChantier,
  rattacherApo,
  renommerDepo,
  supprimerChantier,
} from "./api";
import type { ChantierSupprime, CommuneProposee } from "./api";
import { valeurDeLaZone } from "./valeurZone";

/**
 * Le libellé français d'un statut, ou le code s'il est inconnu.
 *
 * Un code inconnu s'affiche tel quel plutôt que de disparaître : c'est le signe
 * qu'une valeur est entrée en base sans passer par le vocabulaire du produit, et un
 * écran qui l'escamote empêche de s'en apercevoir.
 */
function libelleStatut(code: string): string {
  return code in STATUTS_CHANTIER
    ? STATUTS_CHANTIER[code as keyof typeof STATUTS_CHANTIER]
    : code;
}

/** Le montant du marché d'un chantier : la somme de ses déboursés, avenants compris. */
function montantDuMarche(chantier: ChantierListe): string | null {
  const montants = chantier.depos
    .map((d) => d.montantMarcheHT)
    .filter((m): m is string => m !== null);
  if (montants.length === 0) return null;
  // Somme en centimes entiers : additionner des chaînes décimales en flottant est
  // exactement ce que le reste du produit s'interdit.
  const centimes = montants.reduce((total, m) => total + Math.round(Number(m) * 100), 0);
  return (centimes / 100).toFixed(2);
}

/* ---------------------------------------------------------------------------
 * La fiche
 * ------------------------------------------------------------------------- */

/**
 * Ce que la fiche tient en cours de saisie, avant enregistrement.
 *
 * **Les champs déduits (058) n'y figurent que s'ils ont été CORRIGÉS.** `distances` ne porte
 * qu'un kilométrage saisi à la main, `zoneIndemniteId` et `zonePanier` qu'un choix explicite.
 * Ce qui est vide se lit « laisse le calcul faire » — et c'est ce qui permet à une table de
 * distances qu'on est en train d'alimenter de continuer à se voir sur les chantiers.
 *
 * Le contraire — recopier les valeurs affichées dans le brouillon — les aurait renvoyées au
 * serveur à chaque enregistrement, donc figées.
 */
type Brouillon = {
  adresse: string;
  codePostal: string;
  latitude: string;
  longitude: string;
  dateDebut: string;
  dateDebutStatut: string;
  statut: string;
  /** La commune choisie dans la liste fermée, `null` si on n'y touche pas. */
  communeId: string | null;
  /** Le texte du champ ville : une recherche, pas une saisie libre. */
  ville: string;
  /** Les distances corrigées à la main, par site. Une entrée absente = celle de la table. */
  distances: Record<string, string>;
  /** La zone d'indemnité choisie à la main, `null` = celle que la distance désigne. */
  zoneIndemniteId: string | null;
  /** `""` = calculé, `NON_APPLICABLE` = NA forcé, sinon l'identifiant d'une zone. */
  zonePanier: string;
};

/** Ce que porte le choix « non applicable » du panier dans la liste déroulante. */
const NON_APPLICABLE = "NA";

function brouillonDe(chantier: ChantierListe): Brouillon {
  return {
    adresse: chantier.adresse.adresse ?? "",
    codePostal: chantier.adresse.codePostal ?? "",
    latitude: chantier.adresse.latitude ?? "",
    longitude: chantier.adresse.longitude ?? "",
    dateDebut: chantier.dateDebut ?? "",
    dateDebutStatut: chantier.dateDebutStatut,
    // `statutDeclare`, et surtout pas `statut` : depuis le 19/08/2026 celui qui s'affiche est
    // déduit, et le renvoyer au serveur ferait échouer l'enregistrement de toute la fiche —
    // adresse comprise — au motif d'un statut que personne n'a voulu changer.
    statut: chantier.statutDeclare,
    // Rien à envoyer tant qu'aucune commune n'a été choisie : la ville rattachée ne change pas
    // parce qu'on a ouvert la fiche pour corriger une date.
    communeId: null,
    ville: chantier.commune?.nom ?? chantier.adresse.ville ?? "",
    distances: Object.fromEntries(
      chantier.distances.filter((d) => d.corrigee).map((d) => [d.siteId, d.distanceKm]),
    ),
    zoneIndemniteId: chantier.zones.indemnite.forcee
      ? (chantier.zones.indemnite.zone?.id ?? null)
      : null,
    zonePanier: !chantier.zones.panier.forcee
      ? ""
      : (chantier.zones.panier.zone?.id ?? NON_APPLICABLE),
  };
}

/**
 * Ce qu'une commune propose comme étiquette dans la liste : son nom ET son code postal.
 *
 * Le nom seul ne suffit pas — 76 noms se répètent dans le référentiel, jusqu'à quatre fois — et
 * c'est aussi ce qui permet de retrouver la commune choisie dans les propositions : un champ à
 * liste (`datalist`) écrit dans le champ le `value` de l'option retenue, et rien d'autre.
 */
function etiquetteCommune(commune: CommuneProposee): string {
  return `${commune.nom} (${commune.codePostal})`;
}

/**
 * Ce que le texte du champ ville désigne, parmi les communes déjà vues.
 *
 * ## Pourquoi l'étiquette exacte ne suffit PAS
 *
 * *Chris, 02/09/2026, en essayant l'écran : « j'ai sélectionné la ville Maillas et du coup cela
 * ne remplit pas les champs latitude, longitude », puis « le champ code postal non plus n'est
 * pas automatiquement rempli ».* Le second symptôme a donné la cause : rien ne s'était rempli
 * **parce que la commune n'avait pas été reconnue**.
 *
 * La première version n'acceptait que l'étiquette de la liste, `Maillas (40120)`. Or on tape le
 * nom, on voit la suggestion, et on continue : le champ contient alors `maillas`, qui n'est
 * l'étiquette de rien. L'écran ne remplissait rien et ne disait rien — le pire des deux.
 *
 * ## Trois réponses, et la troisième est celle qui compte
 *
 *   1. **l'étiquette exacte** — le chemin du clic dans la liste ;
 *   2. **un nom qui ne désigne qu'une commune** — le chemin de la frappe. C'est le cas courant
 *      et il doit marcher ;
 *   3. **un nom que plusieurs communes portent** : on ne devine **pas**, et on le dit. 76 noms
 *      se répètent dans le référentiel, jusqu'à quatre fois — quatre Mazerolles, quatre
 *      Montaut. Trancher au hasard rattacherait un chantier à la mauvaise commune, donc à la
 *      mauvaise distance et à la mauvaise indemnité, sans que rien ne le signale. C'est la
 *      panne des absences du 16/08 sous une autre forme, et la seule sortie est le code postal
 *      — que l'étiquette porte.
 *
 * La comparaison est insensible à la casse mais **pas aux accents** : `unaccent` n'est pas
 * installé sur le serveur, la recherche elle-même ne les ignore pas, et faire ici l'inverse de
 * ce que fait la recherche donnerait un champ qui reconnaît un nom que la liste n'a pas proposé.
 */
/**
 * Ce que le choix d'une commune écrit dans le brouillon.
 *
 * Une fonction à part parce que **deux chemins y mènent** : la frappe, et l'arrivée des
 * propositions un instant plus tard (voir l'effet de recherche). Les deux doivent remplir
 * exactement les mêmes champs, sinon l'un des deux laisserait un code postal en arrière.
 *
 * Une commune sans coordonnées **n'efface pas** celles du chantier *(« si tu n'as pas de
 * coordonnées tu ne mets rien »)* : le point utile est l'entrée du chantier, pas le centre de
 * la commune, et 1 369 communes sur 4 052 n'en portent aucune.
 */
function patchDeLaCommune(commune: CommuneProposee): Partial<Brouillon> {
  return {
    ville: commune.nom,
    communeId: commune.id,
    codePostal: commune.codePostal,
    ...(commune.latitude !== null && commune.longitude !== null
      ? { latitude: commune.latitude, longitude: commune.longitude }
      : {}),
  };
}

export function resoudreCommune(
  texte: string,
  connues: readonly CommuneProposee[],
):
  | { readonly trouvee: CommuneProposee }
  | { readonly homonymes: readonly CommuneProposee[] }
  | null {
  const saisi = texte.trim();
  if (saisi === "") return null;

  const parEtiquette = connues.find((c) => etiquetteCommune(c) === saisi);
  if (parEtiquette !== undefined) return { trouvee: parEtiquette };

  const parNom = connues.filter((c) => c.nom.toLowerCase() === saisi.toLowerCase());
  if (parNom.length === 1 && parNom[0] !== undefined) return { trouvee: parNom[0] };
  if (parNom.length > 1) return { homonymes: parNom };
  return null;
}

/*
  `valeurDeLaZone` a déménagé dans `valeurZone.ts` le 18/09/2026 : la colonne « Zone transport » de
  la saisie des heures propose les mêmes tranches, et deux façons d'écrire le même barème dans deux
  écrans se liraient comme deux barèmes.
*/

/**
 * Pourquoi il n'y a pas de zone, en une phrase qui dit OÙ corriger.
 *
 * `non_applicable` est la seule qui ne soit pas un défaut : c'est la réponse du métier sous la
 * borne basse du barème *(« sous 50 km tu mets Non applicable = NA »)*. Les quatre autres se
 * corrigent à quatre endroits différents, et un tiret unique ferait chercher au mauvais.
 */
const SANS_ZONE: Record<SansZone, string> = {
  pas_de_site_de_reference:
    "aucun site n'est désigné « référence pour les zones » au référentiel des carrières et sites",
  distance_a_calculer: "la distance n'est pas connue : la saisir dans le chantier, au champ du site",
  non_applicable: "NA — non applicable",
  aucun_bareme: "aucun barème n'est en vigueur à cette date (menu Paramétrage transport)",
  hors_bareme: "aucune zone du barème ne couvre cette distance",
};

/**
 * Ce qu'il manque au chantier pour être validé, en toutes lettres.
 *
 * Le serveur envoie ces manques calculés ; l'écran ne fait que les traduire. Les recalculer
 * ici — « pas de date de démarrage, donc… » — serait une seconde implémentation de la règle,
 * et elle divergerait au premier ajustement.
 */
const MANQUES: Record<string, string> = {
  validation: "les validations du circuit VAL-CE",
  date_de_demarrage: "une date de démarrage",
};

/**
 * Les références des déboursés du chantier, corrigeables.
 *
 * *Demandé par Chris le 19/08/2026, en découvrant « SANS-REFERENCE-0 » dans sa liste de
 * versions.* L'import cherche un motif `DEPO…` dans le classeur ; quand il n'en trouve aucun, il
 * pose ce nom de repli, qui ne dit rien à personne.
 *
 * **Un bouton par déboursé, et non le « Enregistrer » de la fiche.** Les deux ne portent pas sur
 * le même objet : la fiche décrit le chantier, la référence appartient à l'un de ses déboursés,
 * et un chantier en a plusieurs. Les faire partir ensemble obligerait à deviner, à la lecture du
 * bouton, ce qui est enregistré.
 */
function References({
  chantier,
  onEnregistre,
}: {
  chantier: ChantierListe;
  onEnregistre: () => void;
}): JSX.Element | null {
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  const [occupe, setOccupe] = useState<string | null>(null);
  const [dit, setDit] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  if (chantier.depos.length === 0) return null;

  const valeur = (depo: DepoListe): string => saisies[depo.id] ?? depo.numeroDepo;

  const corriger = (depo: DepoListe): void => {
    setOccupe(depo.id);
    setErreur(null);
    setDit(null);
    renommerDepo(depo.id, valeur(depo))
      .then(({ modifie }) => {
        setDit(modifie ? "Référence corrigée." : "Rien n'avait changé.");
        // La liste est rechargée : la référence s'affiche aussi dans le sélecteur de version,
        // et deux endroits qui la montrent ne doivent pas en montrer deux états.
        onEnregistre();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  return (
    <div className="fiche__references">
      <h4>Référence des déboursés</h4>
      <p className="note">
        C'est le n° <code>DEPO…</code> lu dans le classeur à l'import. Quand il n'y figure pas,
        l'application pose <code>SANS-REFERENCE-{"<rang>"}</code> : corrigez-le ici plutôt que de
        réimporter le chantier, ce qui emporterait la contre-étude et le planning. Cette
        référence n'est qu'une étiquette — aucun calcul ne la lit.
      </p>
      {chantier.depos.map((depo) => (
        <div key={depo.id} className="fiche__reference">
          <label>
            {depo.rang === 0 ? "Déboursé initial" : `Complémentaire n° ${String(depo.rang)}`}
            <input
              className="saisie saisie--texte"
              value={valeur(depo)}
              placeholder="DEPO00000123"
              maxLength={60}
              onChange={(e) => {
                const saisie = e.target.value;
                setSaisies((courant) => ({ ...courant, [depo.id]: saisie }));
                setDit(null);
              }}
            />
          </label>
          <button
            type="button"
            disabled={occupe !== null || valeur(depo).trim() === depo.numeroDepo}
            onClick={() => {
              corriger(depo);
            }}
          >
            {occupe === depo.id ? "Correction…" : "Corriger"}
          </button>
          {depo.fichierNom !== null && (
            <span className="note">importé de {depo.fichierNom}</span>
          )}
        </div>
      ))}
      {dit !== null && <p className="etat">{dit}</p>}
      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
    </div>
  );
}

/**
 * Un champ de zone : ce que le barème répond, et la possibilité de trancher autrement.
 *
 * ## Le premier choix est « calculé », et ce n'est pas un détail
 *
 * Tant qu'il est retenu, rien n'est stocké : la zone suit la distance du chantier — celle figée à
 * l'enregistrement de sa ville (093), ou celle qu'on y a corrigée.
 *
 * Le libellé du choix « calculé » **dit ce que le calcul donne** — « calculé : Zone2 (3,71 €) »
 * — plutôt qu'un « — automatique — » muet : sans cela, il faudrait choisir pour savoir ce qu'on
 * abandonne.
 *
 * ## Les tranches proposées sont celles de LA PÉRIODE du chantier
 *
 * Un barème est daté. Proposer les tranches de 2026 sur un chantier qui démarre en 2027 ferait
 * choisir un montant qui n'a plus cours — et le libellé « Zone2 » ne dit pas de quelle année il
 * parle.
 */
function Zone({
  libelle,
  retenue,
  bareme,
  choix,
  nonApplicable = false,
  onChoisir,
}: {
  libelle: string;
  retenue: ZoneRetenue;
  bareme: readonly BaremePeriode[];
  choix: string;
  nonApplicable?: boolean;
  onChoisir: (valeur: string) => void;
}): JSX.Element {
  const periode = bareme.find((p) => p.periodeId === retenue.periodeId);

  /*
    Ce que le CALCUL donne, indépendamment de ce qui est choisi. Quand une zone est forcée, le
    serveur ne renvoie plus la déduction — il renvoie le choix — donc on ne peut l'afficher que
    dans le cas contraire. Le dire ainsi vaut mieux que de le recalculer ici : ce serait une
    seconde implémentation de la règle des bornes, et elle divergerait au premier ajustement.
  */
  const calcul = retenue.forcee
    ? null
    : retenue.zone !== null
      ? `${retenue.zone.libelle} (${valeurDeLaZone(retenue.zone)})`
      : SANS_ZONE[retenue.sansZone ?? "hors_bareme"];

  return (
    <label>
      {libelle}
      {/*
        Vert en permanence comme les cinq autres champs déduits : la légende de la fiche parle de
        la NATURE du champ — il se remplit tout seul —, pas de l'état de sa valeur. Ce qui
        signale un choix à la main, c'est la mention « choisi à la main » ci-dessous.
      */}
      <select
        className="saisie--deduite"
        value={choix}
        onChange={(e) => {
          onChoisir(e.target.value);
        }}
      >
        <option value="">
          {calcul === null ? "— calculé —" : `— calculé : ${calcul} —`}
        </option>
        {nonApplicable && <option value={NON_APPLICABLE}>NA — non applicable</option>}
        {(periode?.zones ?? []).map((zone) => (
          <option key={zone.id} value={zone.id}>
            {zone.libelle} — {valeurDeLaZone(zone)}
          </option>
        ))}
      </select>
      {retenue.forcee && (
        <span className="note">
          choisi à la main
          {retenue.zone !== null && ` : ${valeurDeLaZone(retenue.zone)}`}
          {retenue.zone === null && retenue.sansZone === "non_applicable" && " : NA"}
        </span>
      )}
      {!retenue.forcee && retenue.sansZone !== null && retenue.sansZone !== "non_applicable" && (
        <span className="note note--alerte">{SANS_ZONE[retenue.sansZone]}</span>
      )}
    </label>
  );
}

function Fiche({
  chantier,
  baremes,
  sitesSansTableDeDistances,
  onEnregistre,
}: {
  chantier: ChantierListe;
  baremes: BaremesTransport;
  /**
   * Combien de sites actifs n'ont pas de table de distances, donc ne figurent pas ici.
   *
   * La fiche montre désormais TOUS les sites : un site du référentiel qui n'y apparaît pas se
   * lirait comme un oubli, et on irait le chercher dans une variable de configuration où il
   * n'est plus question. Le compteur le dit et nomme l'endroit où créer la table.
   */
  sitesSansTableDeDistances: number;
  onEnregistre: () => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<Brouillon>(() => brouillonDe(chantier));
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [dit, setDit] = useState<string | null>(null);
  /** Les communes proposées pour ce qu'on tape, et le texte cherché (voir l'effet ci-dessous). */
  const [communes, setCommunes] = useState<readonly CommuneProposee[]>([]);
  const [cherche, setCherche] = useState("");
  /**
   * Toutes les communes vues depuis l'ouverture de la fiche, par étiquette.
   *
   * **Elle S'ACCUMULE, elle ne se remplace pas**, et c'est le défaut que le dépôt a déjà payé
   * une fois : chercher la commune choisie dans les *dernières* propositions ne marche que
   * tant que la liste est encore celle-là — et elle ne l'est plus dès qu'une recherche part
   * pour le texte que le choix vient d'écrire. C'est la même leçon que `codeArticle.ts` au
   * déboursé, et la même panne : le champ garde son texte, et rien ne se remplit.
   *
   * Une étiquette proposée l'a forcément été par une recherche antérieure : la retrouver ici
   * est donc toujours possible, quel que soit ce que la liste affiche à l'instant.
   */
  const [connues, setConnues] = useState<ReadonlyMap<string, CommuneProposee>>(new Map());

  const champ = (nom: keyof Brouillon) => (valeur: string) => {
    setBrouillon((courant) => ({ ...courant, [nom]: valeur }));
    setDit(null);
  };

  /**
   * La recherche de communes, retardée de 200 ms après la dernière frappe.
   *
   * Sans ce délai, taper « saint-perdon » lancerait onze requêtes dont dix sans intérêt, et
   * leurs réponses pourraient revenir dans le désordre — la liste afficherait alors les
   * propositions d'un préfixe déjà abandonné. C'est le même minuteur que la recherche
   * d'articles du déboursé, pour la même raison.
   */
  useEffect(() => {
    const terme = cherche.trim();
    if (terme.length < 2) {
      setCommunes([]);
      return;
    }
    let abandonne = false;
    const minuteur = setTimeout(() => {
      chercherCommunes(terme)
        .then(({ communes: trouvees }) => {
          if (abandonne) return;
          setCommunes(trouvees);
          setConnues((deja) => {
            const toutes = new Map(deja);
            for (const c of trouvees) toutes.set(etiquetteCommune(c), c);
            return toutes;
          });

          /*
            ON RÉSOUT AUSSI ICI, et c'est le défaut que Chris a rencontré.

            La résolution avait lieu à la frappe seulement. Or les propositions arrivent 200 ms
            plus tard : quand on tape « maillas » et qu'on s'arrête, la liste est vide au moment
            où le texte est saisi, et **rien ne réévalue** quand elle arrive. Le champ gardait
            son texte, le code postal restait vide, les distances ne venaient pas — et l'écran
            n'avait rien à dire puisqu'il n'avait rien constaté.

            Pas de boucle : rattacher réécrit `ville` avec le nom canonique et vide `cherche`,
            donc cet effet repart et sort immédiatement.
          */
          const issue = resoudreCommune(terme, trouvees);
          if (issue !== null && "trouvee" in issue) {
            setBrouillon((courant) => ({ ...courant, ...patchDeLaCommune(issue.trouvee) }));
            setCherche("");
          }
        })
        .catch(() => {
          // Une recherche qui échoue ne doit pas casser la fiche : la liste reste vide, et le
          // champ garde ce qui est saisi. L'enregistrement, lui, dira ce qui cloche.
          if (!abandonne) setCommunes([]);
        });
    }, 200);
    return () => {
      abandonne = true;
      clearTimeout(minuteur);
    };
  }, [cherche]);

  /**
   * Le champ ville est un SÉLECTEUR, pas une saisie libre.
   *
   * *« La liste est volontairement fermée : si on ne trouve pas la ville cela veut dire qu'il
   * faut aller créer la ville dans les tables distance et à ce moment-là on pourra la
   * sélectionner »* (métier, 02/09/2026).
   *
   * Choisir dans la liste remplit le code postal et les coordonnées **tout de suite**, pour que
   * la saisie se voie ; le serveur les réécrit depuis la commune à l'enregistrement, et c'est
   * lui qui fait foi. Taper un texte qui ne correspond à rien ne détache pas la commune
   * actuelle : ça ne change rien, et la note sous le champ dit pourquoi.
   */
  const saisirVille = (texte: string): void => {
    setDit(null);
    // Dans TOUTES les communes vues, jamais dans les seules dernières propositions : voir
    // `connues` ci-dessus. Et par étiquette OU par nom sans ambiguïté : voir `resoudreCommune`.
    const issue = resoudreCommune(texte, [...connues.values()]);

    if (issue === null || "homonymes" in issue) {
      setBrouillon((courant) => ({ ...courant, ville: texte, communeId: null }));
      setCherche(texte);
      return;
    }

    setBrouillon((courant) => ({ ...courant, ...patchDeLaCommune(issue.trouvee) }));
    setCherche("");
  };

  /** Ce que la ville saisie désigne : reconnu, ambigu, ou introuvable. */
  const issueDeLaVille = resoudreCommune(brouillon.ville, [...connues.values()]);

  /**
   * La commune retenue, telle qu'on peut en parler — et **seulement si le texte du champ la
   * désigne encore**.
   *
   * C'est la correction d'un mensonge attrapé au navigateur : la version d'avant retombait sur
   * la commune enregistrée dès que le texte tapé ne résolvait pas. La fiche affichait donc
   * « rattachée à Bourg-Lointain » pendant qu'on lisait « maillas » dans le champ juste
   * au-dessus, et les coordonnées de l'une passaient pour celles de l'autre.
   *
   * La commune enregistrée ne compte donc que tant que le champ porte encore son nom. Dès qu'on
   * tape autre chose, il n'y a plus de commune retenue — et la ligne d'état dit « pas encore
   * reconnue », ce qui est la vérité.
   */
  const communeRetenue: CommuneChantier | CommuneProposee | null =
    issueDeLaVille !== null && "trouvee" in issueDeLaVille
      ? issueDeLaVille.trouvee
      : chantier.commune !== null &&
          brouillon.ville.trim().toLowerCase() === chantier.commune.nom.toLowerCase()
        ? chantier.commune
        : null;

  /**
   * Le champ porte-t-il encore ce que la commune a rempli ?
   *
   * *Chris, 02/09/2026 : « les champs code postal, latitude et longitude […] doivent aussi être
   * en vert clair car ils doivent se remplir automatiquement en fonction de la ville. Il faut
   * pouvoir juste les modifier si besoin. »*
   *
   * Le vert ne se pose donc pas « au moment du choix » mais **tant que la valeur est celle de la
   * commune** — c'est la même règle que les distances, et elle a le même effet heureux : modifier
   * le champ le rend neutre, le remettre à la valeur de la commune le reverdit. Aucun drapeau à
   * tenir, donc rien qui puisse mentir.
   *
   * La comparaison des coordonnées est **numérique** : la base rend « 44.837800 » et une saisie
   * à la main donne « 44.8378 ». Comparer les textes ferait passer la même valeur pour une
   * correction. Le code postal, lui, est du texte de bout en bout (016) — « 01000 » perdrait son
   * zéro de tête au passage par un nombre.
   */
  const vientDeLaCommune = (champ: "codePostal" | "latitude" | "longitude"): boolean => {
    if (communeRetenue === null) return false;
    const saisi = brouillon[champ].trim();
    const source = communeRetenue[champ];
    if (saisi === "" || source === null) return false;
    if (champ === "codePostal") return saisi === source;
    return Number(saisi) === Number(source);
  };

  /**
   * Le champ a-t-il été corrigé à la main, alors qu'une commune le renseignait ?
   *
   * **Le fond vert est désormais permanent** sur les cinq champs déduits *(Chris, 02/09/2026 :
   * « le champ code postal fond vert clair, idem pour latitude et longitude et distance … »)* :
   * il dit ce que le champ EST — rempli automatiquement — et non l'état de sa valeur. Avec le
   * rouge de la ville, les deux couleurs forment une légende : on commence là, le reste suit.
   *
   * Mais alors le vert ne signale plus une correction, et une valeur corrigée passerait pour
   * celle de la commune. D'où cette mention : c'est elle qui porte l'information que la couleur
   * ne porte plus. Les distances, elles, ont déjà leur bouton « revenir à la table ».
   */
  const corrigeALaMain = (champ: "codePostal" | "latitude" | "longitude"): boolean =>
    communeRetenue !== null &&
    communeRetenue[champ] !== null &&
    brouillon[champ].trim() !== "" &&
    !vientDeLaCommune(champ);

  /** La distance affichée pour un site : celle qu'on a corrigée, sinon celle du calcul. */
  const distanceAffichee = (site: DistanceSiteChantier): string =>
    brouillon.distances[site.siteId] ?? site.distanceKm;

  const corrigerDistance = (siteId: string, valeur: string): void => {
    setBrouillon((courant) => ({
      ...courant,
      distances: { ...courant.distances, [siteId]: valeur },
    }));
    setDit(null);
  };

  const rendreAuCalcul = (siteId: string): void => {
    setBrouillon((courant) => {
      const { [siteId]: _retiree, ...reste } = courant.distances;
      return { ...courant, distances: reste };
    });
    setDit(null);
  };

  const enregistrer = (): void => {
    setEnAttente(true);
    setErreur(null);
    setDit(null);

    const { communeId, ville: _ville, distances, zonePanier, ...champs } = brouillon;

    /*
      CE QUI PART, ET CE QUI NE PART PAS.

      Les champs ordinaires partent tous, y compris les inchangés : c'est le serveur qui compare
      et n'écrit que la différence, et lui seul connaît l'état réel de la base.

      Les champs déduits (058), non. Ne partent que les valeurs **corrigées à la main** :

        * `ville` ne part jamais — c'est une recherche. La ville en base est celle de la commune
          désignée, et le serveur la réécrit depuis elle. Envoyer le texte du champ ferait
          entrer en base une ville qui ne correspond à aucune commune, ce que la liste fermée
          existe pour empêcher ;
        * `communeId` ne part que si on en a choisi une : ouvrir la fiche pour corriger une date
          ne doit pas rattacher ni détacher une ville ;
        * les distances partent **toutes**, mais à `null` pour celles qu'on n'a pas corrigées —
          ce qui efface une ancienne correction et rend le champ au calcul. C'est ce qui fait
          que « annuler ma correction » se dit en base.
    */
    modifierChantier(chantier.id, {
      ...champs,
      ...(communeId !== null && { communeId }),
      distances: chantier.distances.map((site) => ({
        siteId: site.siteId,
        distanceKm: distances[site.siteId] ?? null,
      })),
      zonePanierId: zonePanier === "" || zonePanier === NON_APPLICABLE ? null : zonePanier,
      zonePanierSansObjet: zonePanier === NON_APPLICABLE,
    })
      .then(({ modifie }) => {
        setDit(modifie ? "Enregistré." : "Rien n'avait changé.");
        onEnregistre();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  /**
   * Ce qui empêcherait le chantier de démarrer le jour saisi, dit tout de suite.
   *
   * Le champ n'est pas refusé pour autant : une date de démarrage un dimanche est une
   * information — c'est peut-être la date du marché —, et le planning démarrera le
   * lundi. Ce qui serait fautif, c'est de décaler en silence.
   */
  const empechementDuDebut = !estDateCivile(brouillon.dateDebut)
    ? null
    : jourDeLaSemaine(brouillon.dateDebut) >= 6
      ? "un week-end"
      : ((nom) => (nom === null ? null : `un jour férié (${nom})`))(ferie(brouillon.dateDebut));

  return (
    <div className="fiche">
      <div className="fiche__grille">
        <label className="fiche__large">
          Adresse
          <input
            className="saisie saisie--texte"
            value={brouillon.adresse}
            placeholder="Voie, lieu-dit, accès"
            onChange={(e) => champ("adresse")(e.target.value)}
          />
        </label>

        {/*
          Fond vert clair tant que la valeur est celle de la commune choisie, neutre dès qu'on
          la corrige : voir `vientDeLaCommune`. Le code postal, la latitude et la longitude se
          remplissent au choix de la ville *(Chris, 02/09/2026)* et restent modifiables — le
          point utile n'est pas toujours celui de la commune, une entrée de piste n'a pas de
          numéro dans la rue (13/08).
        */}
        <label>
          Code postal
          <input
            className="saisie saisie--texte saisie--deduite"
            value={brouillon.codePostal}
            placeholder="40000"
            inputMode="numeric"
            maxLength={5}
            onChange={(e) => champ("codePostal")(e.target.value)}
          />
          {corrigeALaMain("codePostal") && (
            <span className="note">corrigé à la main — la commune dit {communeRetenue?.codePostal}</span>
          )}
        </label>

        {/*
          LA VILLE SE CHERCHE DANS LES TABLES DE DISTANCES (058).

          *« Quand je tape les premières lettres de la ville cela filtre automatiquement le nom
          de la ville ; si je sélectionne une ville cela met automatiquement à jour le champ code
          postal, latitude et longitude »* — et la liste est fermée.

          Un champ à liste (`datalist`) plutôt qu'une liste déroulante : 4 052 communes ne
          tiennent pas dans un `select`, et c'est en tapant qu'on les trouve. L'étiquette porte
          le code postal parce que 76 noms se répètent dans le référentiel.
        */}
        {/*
          LE CHAMP EN ROUGE, et les cinq en vert forment une légende — voir `.fiche__consigne`
          dans la feuille de style. *Chris, 02/09/2026 : « je veux que le champ Ville […] soit
          bien identifié en rouge avec un petit explicatif "sélectionner la ville du
          chantier" ».*

          Le rouge ne dit pas « erreur » ici, il dit « c'est par là qu'on commence » : c'est le
          seul champ de ce groupe qu'on remplit soi-même, les cinq autres en découlent.
        */}
        <label>
          Ville
          <input
            className="saisie saisie--texte saisie--a-selectionner"
            list={`communes-${chantier.id}`}
            value={brouillon.ville}
            placeholder="Taper les premières lettres"
            onChange={(e) => {
              saisirVille(e.target.value);
            }}
          />
          <datalist id={`communes-${chantier.id}`}>
            {communes.map((c) => (
              <option key={c.id} value={etiquetteCommune(c)}>
                {c.codeDepartement === null ? "" : `dép. ${c.codeDepartement}`}
              </option>
            ))}
          </datalist>
          {/*
            CE QUE LA VILLE DÉSIGNE, EN TOUTES LETTRES.

            *Chris, 02/09/2026 : « j'ai sélectionné la ville Maillas et du coup cela ne remplit
            pas les champs latitude, longitude », « le champ code postal non plus ».* Deux
            causes, et le silence de l'écran était la pire : rien ne disait si la commune avait
            été reconnue, ni qu'elle n'avait pas de coordonnées au référentiel.

            Un fond vert ne dit pas LAQUELLE est rattachée. Cette ligne le dit, et elle dit
            aussi ce qui manque — c'est la règle de la donnée manquante : ce qui n'est pas connu
            se voit, il ne se devine pas.
          */}
          {issueDeLaVille !== null && "homonymes" in issueDeLaVille ? (
            <span className="note note--alerte">
              {issueDeLaVille.homonymes.length} communes portent ce nom — choisir dans la liste,
              le code postal les distingue
            </span>
          ) : communeRetenue !== null && brouillon.ville.trim() !== "" ? (
            // Le manque de coordonnées n'est PAS répété ici : la note sous la grille le dit en
            // entier, avec l'endroit où corriger. Deux fois la même phrase allonge la ligne
            // d'état sans rien apprendre.
            <span className="note">
              rattachée à {communeRetenue.nom} ({communeRetenue.codePostal})
            </span>
          ) : brouillon.ville.trim() === "" ? (
            // La consigne demandée par Chris : elle ne s'affiche que tant qu'il n'y a rien à
            // dire de mieux. Une fois la ville rattachée, c'est SON NOM qui compte, pas le
            // rappel de ce qu'il fallait faire — une consigne qui reste après avoir été suivie
            // finit par ne plus se lire du tout.
            <span className="note fiche__consigne">Sélectionner la ville du chantier</span>
          ) : (
            <span className="note note--alerte">pas encore reconnue</span>
          )}
        </label>

        {/*
          Les coordonnées se saisissent à la main, et se collent le plus souvent depuis
          une carte. La virgule décimale est acceptée par le serveur : un clavier
          français la produit, et refuser « 43,9327 » ferait passer l'application pour
          cassée alors que la valeur est bonne.

          Depuis la 058, choisir une commune les remplit — d'où le fond vert clair tant que la
          commune vient d'être choisie. Elles restent modifiables : le point utile n'est pas
          toujours celui de la commune, une entrée de piste n'a pas de numéro dans la rue.
        */}
        <label>
          Latitude
          <input
            className="saisie saisie--texte saisie--deduite"
            value={brouillon.latitude}
            placeholder="43.9327"
            onChange={(e) => champ("latitude")(e.target.value)}
          />
          {corrigeALaMain("latitude") && <span className="note">corrigée à la main</span>}
        </label>

        <label>
          Longitude
          <input
            className="saisie saisie--texte saisie--deduite"
            value={brouillon.longitude}
            placeholder="-0.4419"
            onChange={(e) => champ("longitude")(e.target.value)}
          />
          {corrigeALaMain("longitude") && <span className="note">corrigée à la main</span>}
        </label>

        {/*
          LES DISTANCES, une par site portant une table de distances (058, élargi le 08/09/2026).

          *« Nous avons déjà fait apparaître les champs distance de deux sites, il faut rajouter
          les champs de tous les sites »* (Chris). La configuration ne dit plus lesquels
          s'affichent — elle dit lesquels viennent en tête, et lequel désigne les zones.

          Le libellé du champ est la **dénomination du référentiel** et non un nom écrit ici :
          *« tout doit être libellé de la même manière »*. Renommer un site au référentiel
          renomme donc ce champ, et rien ne peut diverger.

          Fond vert clair = la valeur figée pour ce chantier, recopiée de la table du menu Distance
          à l'enregistrement de sa ville (093) : une mise à jour des tables ne la change plus. Dès
          qu'on la corrige — dans le chantier seul, jamais dans la table —,
          le fond redevient neutre et un bouton rend le champ au calcul : sans lui, une
          correction faite par erreur ne se déferait plus.

          FOND BLEU = LE SITE LE PLUS PROCHE. *« Cela doit donner visuellement de manière très
          rapide la carrière depuis laquelle il faut effectuer les transports, et donc aiguiller
          le conducteur sur le choix »* (Chris). Il porte sur le LABEL et non sur le seul champ :
          ce qu'on cherche du regard est le nom du site, pas le nombre.
        */}
        {chantier.distances.map((site) => {
          const corrigee = brouillon.distances[site.siteId] !== undefined;
          return (
            <label
              key={site.siteId}
              className={site.leplusProche ? "fiche__site--proche" : undefined}
              title={
                site.leplusProche
                  ? "Site le plus proche du chantier : c'est de là qu'il faut charger. " +
                    "Cocher « groupe » sur un matériau de carrière le propose tout seul."
                  : site.horsProximite
                    ? "Site de départ : sa distance donne les zones d'indemnité et de panier, " +
                      "mais il ne concourt pas au choix du site le plus proche."
                    : undefined
              }
            >
              Distance {site.denomination}
              {/*
                Vert en permanence, comme le code postal et les coordonnées : la couleur dit que
                le champ se remplit tout seul, pas que sa valeur est intacte. Ce qui signale une
                correction, c'est le bouton « revenir à la table » ci-dessous — il ne s'affiche
                que dans ce cas, et il dit en plus quoi faire.
              */}
              <input
                className="saisie saisie--texte saisie--deduite"
                value={distanceAffichee(site)}
                inputMode="decimal"
                aria-label={`Distance jusqu'à ${site.denomination}, en kilomètres`}
                onChange={(e) => {
                  corrigerDistance(site.siteId, e.target.value);
                }}
              />
              {corrigee ? (
                <button
                  type="button"
                  className="lien"
                  onClick={() => {
                    rendreAuCalcul(site.siteId);
                  }}
                >
                  revenir à la table
                </button>
              ) : (
                <span className="note">
                  {/*
                    Trois causes distinctes à un 0, et elles s'adressent à trois gestes
                    différents. La troisième — « enregistrer d'abord » — vient de l'épreuve au
                    navigateur : après avoir choisi une ville, la fiche disait encore « aucune
                    ville rattachée », ce qui se lit comme un refus alors que la distance
                    n'attend qu'un enregistrement pour arriver.
                  */}
                  {/*
                    LA MARQUE DU PLUS PROCHE VIT DANS LA NOTE, pas dans le libellé.

                    Une couleur seule ne se lit pas, et deux sites à égalité sont tous les deux
                    bleus : le dire en mots évite de croire à un défaut d'affichage. Mais posée
                    dans le libellé, elle passait à la ligne sur un nom de site long, ce qui
                    descendait le champ et DÉSALIGNAIT les distances entre elles — vu en pilotant
                    l'écran. La note existe déjà sous chaque champ : elle porte la mention sans
                    rien déplacer.
                  */}
                  {!site.aCalculer
                    ? `km, figée à l'enregistrement du chantier${site.leplusProche ? " — LE PLUS PROCHE" : ""}${site.reference ? " — désigne les zones" : ""}`
                    : brouillon.communeId !== null
                      ? "enregistrer pour lire la distance de cette ville"
                      : chantier.commune === null
                        ? "aucune ville rattachée"
                        : "distance inconnue : la saisir ici"}
                </span>
              )}
            </label>
          );
        })}

        {/*
          CE QUE LA FICHE NE PEUT PAS MONTRER, dit plutôt que passé sous silence.

          Un site sans table de distances ne peut porter aucune distance — pas « pas encore »,
          jamais : il n'y a rien à remplir au menu Distance tant que la table n'existe pas. Lui
          poser un champ à « 0 à calculer » serait un reproche permanent ; ne rien dire ferait
          chercher un site manquant dans une variable de configuration qui ne le nomme plus.
        */}
        {sitesSansTableDeDistances > 0 && (
          <p className="note fiche__sites-sans-table">
            {sitesSansTableDeDistances === 1
              ? "1 site actif n'a pas de table de distances"
              : `${sitesSansTableDeDistances} sites actifs n'ont pas de table de distances`}{" "}
            : leur distance ne peut pas être lue, et ils n'ont pas de champ ici. La table se crée
            au menu <strong>Distance</strong>.
          </p>
        )}

        {/*
          LES DEUX ZONES (058), lues sur la distance du site de référence.

          *« Tu dois rajouter un champ zone transport, tu sélectionnes la zone dans la table en
          fonction de la distance km […]. Tu rajoutes également le champ panier et tu
          sélectionnes zone A ou B en fonction de la distance. »*

          Une liste déroulante et non un champ texte : ce sont des tranches d'un barème, et une
          zone tapée à la main serait une zone qui n'existe pas. Le premier choix est « calculé »
          — c'est lui qui rend le champ au barème, et c'est l'état normal.
        */}
        <Zone
          libelle="Zone transport"
          retenue={chantier.zones.indemnite}
          bareme={baremes.indemnites}
          choix={brouillon.zoneIndemniteId ?? ""}
          onChoisir={(valeur) => {
            setBrouillon((courant) => ({
              ...courant,
              zoneIndemniteId: valeur === "" ? null : valeur,
            }));
            setDit(null);
          }}
        />

        <Zone
          libelle="Panier"
          retenue={chantier.zones.panier}
          bareme={baremes.paniers}
          choix={brouillon.zonePanier}
          // *« Dans le barème panier sous 50 km tu mets Non applicable = NA. »* NA est une
          // RÉPONSE, donc un choix de la liste — pas l'absence de choix.
          nonApplicable
          onChoisir={(valeur) => {
            setBrouillon((courant) => ({ ...courant, zonePanier: valeur }));
            setDit(null);
          }}
        />

        <label>
          Date de démarrage
          <input
            className="saisie saisie--texte"
            type="date"
            value={brouillon.dateDebut}
            onChange={(e) => champ("dateDebut")(e.target.value)}
          />
        </label>

        <label>
          Régime de la date
          <select
            value={brouillon.dateDebutStatut}
            onChange={(e) => champ("dateDebutStatut")(e.target.value)}
          >
            <option value="hypothese">Hypothèse</option>
            <option value="ferme">Ferme</option>
          </select>
        </label>

        {/*
          Le statut se DÉCLARE, il ne se choisit plus librement (19/08/2026). Trois valeurs
          seulement, plus « CE à traiter » qui vaut « rien de déclaré » — c'est aussi ce qu'on
          repose pour annuler un « arrêté » mis par erreur. Proposer les statuts déduits ferait
          afficher, une seconde après l'enregistrement, autre chose que ce qui vient d'être
          choisi : ça se lit comme une panne.
        */}
        <label>
          Statut déclaré
          <select value={brouillon.statut} onChange={(e) => champ("statut")(e.target.value)}>
            {STATUTS_STOCKABLES.map((code) => (
              <option key={code} value={code}>
                {code === "ce_a_traiter" ? "— aucun (suivre le circuit) —" : libelleStatut(code)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {empechementDuDebut !== null && (
        <p className="note">
          Le {formaterDate(brouillon.dateDebut)} tombe {empechementDuDebut} : le planning
          démarrera au premier jour ouvré suivant, et la date saisie est conservée telle quelle.
        </p>
      )}

      {/*
        Le statut réel, et ce qui le retient. Il est affiché même quand tout va bien : un
        écran qui ne parle qu'en cas de problème laisse croire, le reste du temps, que le
        statut est celui de la liste déroulante juste au-dessus.
      */}
      <p className="note">
        Statut actuel : <strong>{libelleStatut(chantier.statut)}</strong>
        {chantier.manque.length > 0 && (
          <>
            {" — il manque "}
            {chantier.manque.map((m) => MANQUES[m] ?? m).join(" et ")}.
          </>
        )}
        {chantier.manque.length === 0 && chantier.statutDeclare !== "ce_a_traiter" && (
          <> — déclaré à la main : le circuit et la date ne le feront plus bouger.</>
        )}
      </p>

      {/*
        La liste des villes est FERMÉE, et il faut le dire au moment où quelqu'un tape un nom
        qui ne rend rien : sans cette phrase, on croit à une faute de frappe et on recommence.
        Elle ne s'affiche que dans ce cas — une consigne permanente ne se lit plus.
      */}
      {brouillon.communeId === null && cherche.trim().length >= 2 && communes.length === 0 && (
        <p className="note">
          Aucune commune ne correspond à « {cherche.trim()} » dans les tables de distances. La
          liste est fermée : pour la rendre sélectionnable, ajouter cette ville dans le{" "}
          <strong>menu Distance</strong> — elle s'ajoutera à toutes les tables à la fois.
        </p>
      )}

      {/*
        Une commune sans coordonnées, et OÙ les saisir. 1 369 des 4 052 communes du référentiel
        sont dans ce cas — le classeur d'origine ne les portait pas. Le métier l'a tranché le
        02/09 (« si tu n'as pas de coordonnées tu ne mets rien, c'est juste parce que nous
        sommes en train de l'alimenter ») : on n'invente donc rien, mais on dit où corriger,
        sinon le champ vide passe pour une panne de l'écran.
      */}
      {communeRetenue !== null && communeRetenue.latitude === null && (
        <p className="note">
          {communeRetenue.nom} n'a pas de coordonnées au référentiel des communes : latitude et
          longitude restent donc à saisir ici, ou une fois pour toutes dans le{" "}
          <strong>menu Distance</strong>, où elles serviront à tous les chantiers de cette
          commune. La distance, elle, ne dépend pas des coordonnées : elle vient de la table du
          site.
        </p>
      )}

      {/*
        LA LÉGENDE, dite en mots.

        Elle a changé de sens le 02/09/2026 : le vert marquait une valeur *encore conforme au
        calcul* et tombait dès qu'on corrigeait le champ. Chris a demandé qu'il soit permanent —
        « le champ code postal fond vert clair, idem pour latitude et longitude et distance … » —
        parce que ce qu'il veut voir est **quels champs se remplissent tout seuls**, pas lesquels
        n'ont pas encore été touchés.

        Cette phrase devait donc changer avec : elle annonçait « le fond vert clair signale une
        valeur venue du calcul », ce qui est devenu faux dès qu'un champ corrigé reste vert. Une
        légende fausse est pire qu'aucune — c'est elle qu'on croit quand l'écran surprend.
      */}
      <p className="note">
        <strong>Le champ rouge est le seul à remplir</strong> : choisir la ville dans les tables
        du <strong>menu Distance</strong>. Les champs <strong>verts</strong> en découlent — code
        postal, coordonnées, distances, et les deux zones lues dans les barèmes du{" "}
        <strong>Paramétrage transport</strong>. Ils se relisent à chaque ouverture, et restent
        modifiables : ce qu'on corrige le fige pour ce chantier, et l'écran le dit à côté du
        champ. Les zones sont lues au barème en vigueur le{" "}
        {formaterDate(chantier.zones.dateDuBareme)}
        {chantier.dateDebut === null
          ? " (aujourd'hui : le chantier n'a pas de date de démarrage)"
          : " (la date de démarrage du chantier)"}
        .
      </p>

      <p className="note">
        La date de fin ne se saisit pas : elle sort du planning. Le statut non plus, sauf les
        trois ci-dessus : il se déduit des validations du circuit VAL-CE et de la date de
        démarrage (PRD §4.9). Une date posée peut être repoussée, jamais effacée.
      </p>

      <References chantier={chantier} onEnregistre={onEnregistre} />

      <div className="fiche__actions">
        <button type="button" className="principal" disabled={enAttente} onClick={enregistrer}>
          {enAttente ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button
          type="button"
          disabled={enAttente}
          onClick={() => {
            setBrouillon(brouillonDe(chantier));
            setErreur(null);
            setDit(null);
          }}
        >
          Annuler mes modifications
        </button>
        {dit !== null && <span className="etat">{dit}</span>}
      </div>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * La liste
 * ------------------------------------------------------------------------- */

/**
 * La confirmation de suppression : retaper le n° d'affaire.
 *
 * **Pas une boîte « êtes-vous sûr ? »**, qui se clique sans lire — et ce qui part ici
 * est le devis figé, les copies de travail, le planning et l'équipe, sans retour
 * possible. Recopier le n° d'affaire oblige à regarder QUEL chantier on supprime, ce
 * qui est la seule erreur vraiment coûteuse : se tromper de ligne.
 *
 * Elle s'ouvre SOUS la ligne du chantier, comme la fiche : une fenêtre par-dessus
 * cacherait justement le n° qu'on demande de recopier.
 */
function Suppression({
  chantier,
  enAttente,
  onAnnuler,
  onConfirmer,
}: {
  chantier: ChantierListe;
  enAttente: boolean;
  onAnnuler: () => void;
  onConfirmer: () => void;
}): JSX.Element {
  const [saisi, setSaisi] = useState("");
  const concorde = saisi.trim().toUpperCase() === chantier.apo.toUpperCase();

  return (
    <div className="suppression">
      <p className="alerte">
        Supprimer <strong>{chantier.apo}</strong> — {chantier.nom} efface le{" "}
        <strong>devis figé</strong>, toutes les copies de travail et variantes, leurs postes
        et leurs lignes, le planning (barres, livraisons, transports) et l'équipe nommée.{" "}
        <strong>C'est définitif</strong> : rien ne permet de revenir en arrière.
      </p>
      <p className="note">
        Seul le journal en garde la trace — il est en ajout seul et ne s'efface pas, même
        par cette suppression. Pour réimporter ce chantier ensuite, il faudra son fichier
        de déboursé.
      </p>
      <form
        className="suppression__confirmation"
        onSubmit={(e) => {
          e.preventDefault();
          if (concorde && !enAttente) onConfirmer();
        }}
      >
        <label>
          Recopier le n° d'affaire pour confirmer :{" "}
          <input
            className="saisie saisie--texte"
            value={saisi}
            placeholder={chantier.apo}
            disabled={enAttente}
            autoFocus
            onChange={(e) => {
              setSaisi(e.target.value);
            }}
          />
        </label>
        <button type="submit" className="bouton-danger" disabled={!concorde || enAttente}>
          {enAttente ? "Suppression…" : "Supprimer définitivement"}
        </button>
        <button type="button" className="lien" disabled={enAttente} onClick={onAnnuler}>
          Annuler
        </button>
      </form>
    </div>
  );
}

/**
 * Rattacher son APO à une étude du bureau d'études (095).
 *
 * L'APO **remplace** le code ETUDE : la contre-étude et le planning faits par le BE restent, et le
 * chantier sort de la liste réservée au BE pour arriver chez la référente administrative, qui
 * vérifie le chantier et lance le circuit.
 */
function RattachementApo({
  chantier,
  onAnnuler,
  onRattache,
}: {
  chantier: ChantierListe;
  onAnnuler: () => void;
  onRattache: () => void;
}): JSX.Element {
  const [apo, setApo] = useState("");
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  return (
    <div className="confirmation">
      <p>
        <strong>{chantier.apo}</strong> — {chantier.nom} : l&apos;affaire est signée. Son APO
        remplacera le code d&apos;étude ; le travail fait est gardé, et le chantier passera à la
        référente administrative. Ce geste ne se défait pas depuis l&apos;écran.
      </p>
      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setEnAttente(true);
          setErreur(null);
          rattacherApo(chantier.id, apo.trim())
            .then(onRattache)
            .catch((x: unknown) => {
              setErreur(x instanceof Error ? x.message : String(x));
            })
            .finally(() => {
              setEnAttente(false);
            });
        }}
      >
        <label>
          N° d&apos;affaire (APO){" "}
          <input
            value={apo}
            placeholder="APO260098"
            disabled={enAttente}
            onChange={(e) => {
              setApo(e.target.value);
            }}
          />
        </label>
        <button type="submit" disabled={apo.trim() === "" || enAttente}>
          {enAttente ? "Rattachement…" : "Rattacher"}
        </button>
        <button type="button" className="lien" disabled={enAttente} onClick={onAnnuler}>
          Annuler
        </button>
      </form>
    </div>
  );
}

export function Chantiers({
  chantiers,
  baremes,
  sitesSansTableDeDistances,
  onModifie,
  onOuvrir,
}: {
  chantiers: readonly ChantierListe[];
  /** Les deux barèmes de zones (058) : la fiche y prend ses listes déroulantes. */
  baremes: BaremesTransport;
  /** Les sites actifs sans table de distances, que la fiche ne peut pas montrer (08/09/2026). */
  sitesSansTableDeDistances: number;
  /** La liste vient de changer en base : c'est l'appelant qui la relit. */
  onModifie: () => void;
  /** Ouvrir ce chantier dans l'écran demandé. */
  onOuvrir: (chantierId: string, ecran: "contre-etude" | "planning") => void;
}): JSX.Element {
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [aSupprimer, setASupprimer] = useState<string | null>(null);
  const [aRattacher, setARattacher] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Ce que la dernière suppression a effacé : affiché après coup, pas avant. */
  const [supprime, setSupprime] = useState<ChantierSupprime | null>(null);

  const supprimer = (chantier: ChantierListe): void => {
    setEnAttente(true);
    setErreur(null);
    supprimerChantier(chantier.id, chantier.apo)
      .then((bilan) => {
        setSupprime(bilan);
        setASupprimer(null);
        setOuverte(null);
        onModifie();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  /** Le compte rendu de la dernière suppression, valable liste vide ou non. */
  const bilan =
    supprime === null ? null : (
      <p className="etat">
        <strong>{supprime.apo}</strong> — {supprime.nom} supprimé : {supprime.versions} version(s),{" "}
        {supprime.debourses} déboursé(s), {supprime.postes} poste(s), {supprime.lignes} ligne(s),{" "}
        {supprime.barres} barre(s), {supprime.livraisons} livraison(s), {supprime.transports}{" "}
        transport(s), {supprime.affectations} affectation(s).
      </p>
    );

  if (chantiers.length === 0) {
    return (
      <>
        {bilan}
        <p className="alerte">
          Aucun chantier en base : un chantier naît de l'import de son déboursé.
        </p>
      </>
    );
  }

  return (
    <section className="referentiel">
      <h2>Chantiers — {chantiers.length}</h2>

      {bilan}
      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      <p className="note">
        Cliquer un n° d'affaire ouvre sa fiche : adresse, date de démarrage, statut. La{" "}
        <strong>fin prévue</strong> est calculée — dernière barre ou dernière livraison du
        planning, en jours ouvrés depuis la date de démarrage.
      </p>

      {/*
        Le cadre de défilement, comme les référentiels depuis le 01/09/2026.

        C'est lui qui rend la grille ci-dessous effective : `.referentiel__defilement table` pose
        `width: min-content`, ce qui pour une table en `table-layout: fixed` vaut la SOMME des
        largeurs du `colgroup`. Et il fait défiler le TABLEAU seul — sans lui, c'est la page qui
        partirait de côté et le n° d'affaire, seule colonne qui dise de quelle ligne il s'agit,
        sortirait de l'écran.
      */}
      <div className="referentiel__defilement">
      <table className="ressources chantiers">
        {/*
          LA GRILLE DES COLONNES.

          *Chris, 02/09/2026 : « élargis un peu la colonne Chantier et la colonne Statut pour
          qu'on puisse voir les libellés entiers ».*

          Il n'y avait aucune grille : `table.ressources` impose `table-layout: fixed` sur
          `width: 100%`, donc les neuf colonnes se partageaient la fenêtre à parts égales.
          Mesuré au navigateur avant d'y toucher : **neuf colonnes de 128 px**, quelle que soit
          la nature de leur contenu. C'est le défaut réparé le 01/09 sur les référentiels et
          aujourd'hui sur les carrières ; il restait ici.

          Les largeurs sont **mesurées**, pas estimées :

            - `Statut` — « CE validée par resp-exploit » est le plus long des six libellés
              (27 caractères) : c'est lui qui commandait la demande ;
            - `Chantier` — 18 caractères au plus long en production (« PHARMACIE GABARRET »),
              mais le nom est saisi à l'import, donc de la marge ;
            - `Ville` — les communes du référentiel montent à 27 caractères
              (« Port-Sainte-Foy-et-Ponchapt ») ;
            - `Démarrage` et la colonne des gestes étaient **déjà coupées** et personne ne
              l'avait signalé : 217 px et 264 px demandés pour 128 px offerts — donc la pastille
              du régime de date rognée, et le bouton « Supprimer » à moitié hors de sa cellule.
            - `Fin prévue` — 13ch à 18ch, *demande de Chris du 22/09/2026*. La colonne ne porte
              pas qu'une date : « sans date de démarrage » fait 22 caractères, et c'est ce que
              lit tout chantier non démarré — le cas le plus fréquent de la liste.
            - Les gestes — 17rem à 25rem le 27/10/2026 : une étude du BE (095) porte un lien de
              plus, « Rattacher l'APO », et la cellule ne passe pas à la ligne (`nowrap`). Vu à
              l'écran : à 17rem, le lien sortait de la cellule et ne se cliquait plus.
        */}
        <colgroup>
          <col style={{ width: "calc(13ch + 0.7rem)" }} />
          <col style={{ width: "calc(30ch + 0.7rem)" }} />
          <col style={{ width: "calc(26ch + 0.7rem)" }} />
          <col style={{ width: "calc(28ch + 0.7rem)" }} />
          <col style={{ width: "14rem" }} />
          <col style={{ width: "6rem" }} />
          <col style={{ width: "calc(18ch + 0.7rem)" }} />
          <col style={{ width: "calc(14ch + 0.7rem)" }} />
          <col style={{ width: "25rem" }} />
        </colgroup>
        <thead>
          <tr>
            <th>N° d'affaire</th>
            <th>Chantier</th>
            <th>Ville</th>
            <th>Statut</th>
            <th>Démarrage</th>
            <th className="col-nombre">Jours planifiés</th>
            <th>Fin prévue</th>
            <th className="col-nombre">Marché HT</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {chantiers.map((chantier) => {
            const montant = montantDuMarche(chantier);
            const depliee = ouverte === chantier.id;
            return (
              <Fragment key={chantier.id}>
                <tr className={depliee ? "chantiers--ouverte" : undefined}>
                  <td className="code">
                    <button
                      type="button"
                      className="lien"
                      onClick={() => {
                        setOuverte(depliee ? null : chantier.id);
                      }}
                      title="Ouvrir la fiche du chantier"
                    >
                      {depliee ? "▾" : "▸"} {chantier.apo}
                    </button>
                  </td>
                  <td className="designation">{chantier.nom}</td>
                  <td>{chantier.adresse.ville ?? <span className="vide">—</span>}</td>
                  <td>{libelleStatut(chantier.statut)}</td>
                  <td>
                    {chantier.dateDebut === null ? (
                      <span className="vide">sans date</span>
                    ) : (
                      <>
                        {formaterDateLongue(chantier.dateDebut)}{" "}
                        <span
                          className={`regime regime--${chantier.dateDebutStatut}`}
                          title={
                            chantier.dateDebutStatut === "ferme"
                              ? "Date ferme"
                              : "Date hypothèse : elle peut changer à tout moment"
                          }
                        >
                          {chantier.dateDebutStatut === "ferme" ? "ferme" : "hypothèse"}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="col-nombre">
                    {chantier.dernierJourPlanifie === 0 ? (
                      <span className="vide">—</span>
                    ) : (
                      chantier.dernierJourPlanifie
                    )}
                  </td>
                  <td>
                    {chantier.finPrevue !== null ? (
                      formaterDateLongue(chantier.finPrevue)
                    ) : chantier.dernierJourPlanifie === 0 ? (
                      <span className="vide">rien de planifié</span>
                    ) : (
                      <span className="vide">sans date de démarrage</span>
                    )}
                  </td>
                  <td className="col-nombre">
                    {montant === null ? <span className="vide">—</span> : formaterMontant(montant)}
                  </td>
                  <td className="gestes">
                    <button
                      type="button"
                      className="lien"
                      onClick={() => {
                        onOuvrir(chantier.id, "contre-etude");
                      }}
                    >
                      Contre-étude
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="lien"
                      onClick={() => {
                        onOuvrir(chantier.id, "planning");
                      }}
                    >
                      Planning
                    </button>
                    {" · "}
                    {/*
                      Le classeur de TOUTES les versions du chantier, pas de celle qu'on
                      regarde : c'est ce qui permet de comparer les scénarios entre eux.
                      Un lien nu plutôt qu'un `fetch` — le navigateur enregistre le
                      fichier lui-même, avec le nom que le serveur annonce.
                    */}
                    <a
                      className="lien"
                      href={adresseClasseurPlanning(chantier.id)}
                      download
                      title="Exporter en Excel le planning de toutes les versions de ce chantier"
                    >
                      Export Excel
                    </a>
                    {" · "}
                    {/* Une étude du BE (095) se rattache à son APO quand l'affaire est signée :
                        elle devient une affaire et passe à la référente administrative. */}
                    {chantier.apo.toUpperCase().startsWith("ETUDE") && (
                      <>
                        <button
                          type="button"
                          className="lien"
                          disabled={enAttente}
                          title="L'affaire est signée : donner son APO à cette étude"
                          onClick={() => {
                            setErreur(null);
                            setARattacher(aRattacher === chantier.id ? null : chantier.id);
                          }}
                        >
                          Rattacher l&apos;APO
                        </button>
                        {" · "}
                      </>
                    )}
                    {/* Le geste destructeur est le dernier, séparé, et n'ouvre qu'une
                        demande de confirmation — jamais la suppression elle-même. */}
                    <button
                      type="button"
                      className="lien lien--danger"
                      disabled={enAttente}
                      title="Supprimer ce chantier et tout ce qui s'y rattache"
                      onClick={() => {
                        setErreur(null);
                        setASupprimer(aSupprimer === chantier.id ? null : chantier.id);
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
                {depliee && (
                  <tr className="ligne-detail">
                    <td colSpan={9}>
                      <Fiche
                        chantier={chantier}
                        baremes={baremes}
                        sitesSansTableDeDistances={sitesSansTableDeDistances}
                        onEnregistre={onModifie}
                      />
                    </td>
                  </tr>
                )}
                {aRattacher === chantier.id && (
                  <tr className="ligne-detail">
                    <td colSpan={9}>
                      <RattachementApo
                        chantier={chantier}
                        onAnnuler={() => {
                          setARattacher(null);
                        }}
                        onRattache={() => {
                          setARattacher(null);
                          onModifie();
                        }}
                      />
                    </td>
                  </tr>
                )}
                {aSupprimer === chantier.id && (
                  <tr className="ligne-detail">
                    <td colSpan={9}>
                      <Suppression
                        chantier={chantier}
                        enAttente={enAttente}
                        onAnnuler={() => {
                          setASupprimer(null);
                        }}
                        onConfirmer={() => {
                          supprimer(chantier);
                        }}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </section>
  );
}
